import React, { useState } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { MasterDataForm } from '../../components/admin/MasterDataForm';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { StatusMaster } from '../../types/masterData';
import { Plus } from 'lucide-react';

export const StatusesPage: React.FC = () => {
  const { statuses, isLoading, createStatus, updateStatus, deleteStatus } = useAdminConfig();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StatusMaster | null>(null);

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Statuses..." />;

  const fields = [
    { key: 'name', label: 'Status Name', required: true },
    { key: 'stage', label: 'Stage', type: 'select' as const, options: [
      { value: 'INITIAL', label: 'Initial' },
      { value: 'WORKFLOW', label: 'Workflow' },
      { value: 'FINAL', label: 'Final' },
    ], required: true },
    { key: 'color_code', label: 'Color Code', required: true },
  ];

  const handleSave = async (data: Record<string, string>) => {
    if (editing) await updateStatus(editing.status_id, data);
    else await createStatus(data);
    setFormOpen(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-slate-100">Statuses</h3>
        <p className="text-xs text-slate-400">Manage workflow status definitions and stages</p>
      </div>
      <Card title="Workflow Statuses">
        <div className="space-y-2">
          {statuses.map(status => (
            <div key={status.status_id} className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg hover:bg-slate-800/40 transition-colors">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: status.color_code }} />
                <div>
                  <span className="text-xs font-semibold text-slate-200">{status.name}</span>
                  <span className="ml-2 text-[10px] font-mono text-slate-500">{status.status_id}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge label={status.stage} variant={status.stage === 'INITIAL' ? 'info' : status.stage === 'FINAL' ? 'neutral' : 'warning'} />
                <Button variant="ghost" size="sm" onClick={() => { setEditing(status); setFormOpen(true); }}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => deleteStatus(status.status_id)} className="text-rose-400 hover:text-rose-300">Delete</Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} icon={<Plus className="w-3.5 h-3.5" />}>Add New Status</Button>
        </div>
      </Card>
      <MasterDataForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave}
        title={editing ? 'Edit Status' : 'Add New Status'} fields={fields}
        initialData={editing ? { name: editing.name, stage: editing.stage, color_code: editing.color_code } : undefined} />
    </div>
  );
};
